/**
 * Typed node-ID helpers.
 *
 * Canvas node IDs follow a convention: `${kind}-${rawId}`, where `kind` is one of a
 * fixed set of prefixes and `rawId` is the underlying domain ID (e.g. asset UUID, bin
 * UUID). Instead of scattering string-literal `startsWith('asset-')` checks across
 * the codebase, these helpers centralize the prefix → kind mapping and give us a
 * typed `NodeKind` union.
 *
 * Notes
 * - `NodeKind` is the id-prefix namespace, NOT the same thing as `EngineNode.kind`.
 *   Example: an `asset-` id can be a shot, image, audio, or set EngineNode depending
 *   on the asset's media type. `NodeKind` here only tells you what family the id
 *   belongs to (`'asset'`), not how the engine renders it.
 * - `stack-backdrop-xxx` is NOT a stack id. The helpers parse longest-prefix-first
 *   so `isNodeOfKind('stack-backdrop-abc', 'stack')` is `false`, and
 *   `getNodeKind('stack-backdrop-abc')` is `'stackBackdrop'`.
 * - Unknown prefixes return `null` — callers can narrow explicitly.
 *
 * ## ID_PREFIX_REGISTRY
 *
 * All prefix / NodeKind metadata lives in a single `ID_PREFIX_REGISTRY` declared
 * below. Adding a new kind means:
 *   1. Add the name to `NodeKind`
 *   2. Add an entry to `ID_PREFIX_REGISTRY` — TypeScript errors otherwise.
 *   3. (Optionally) add a `NodeKindRegistry` entry + clipboard schema.
 */

export type NodeKind =
  | 'asset'
  | 'job'
  | 'bin'
  | 'annotation'
  | 'character'
  | 'subject'
  | 'collection'
  | 'variable'
  | 'action'
  | 'stack'
  | 'stackBackdrop'
  | 'workflowFrame'
  | 'submission'

/**
 * Declarative contract for a single id-prefix kind. The registry below keys
 * every `NodeKind` to exactly one entry; the three helpers below (`toNodeId`,
 * `parseNodeId`, `getNodeKind`) all derive from this single source.
 */
export interface IdPrefixEntry<K extends NodeKind = NodeKind> {
  /** Discriminator — matches `NodeKind`. */
  kind: K

  /**
   * String prepended to raw domain ids. Includes the trailing `-` so
   * `parseNodeId` can do a clean `startsWith` check. Prefixes must be
   * unique across all entries.
   */
  idPrefix: string
}

/**
 * The single source of truth for id-prefix metadata. `Record<NodeKind, ...>`
 * enforces at compile time that every kind has an entry.
 */
export const ID_PREFIX_REGISTRY: { [K in NodeKind]: IdPrefixEntry<K> } = {
  asset: { kind: 'asset', idPrefix: 'asset-' },
  job: { kind: 'job', idPrefix: 'job-' },
  bin: { kind: 'bin', idPrefix: 'bin-' },
  annotation: { kind: 'annotation', idPrefix: 'annotation-' },
  character: { kind: 'character', idPrefix: 'character-' },
  subject: { kind: 'subject', idPrefix: 'subject-' },
  collection: { kind: 'collection', idPrefix: 'collection-' },
  variable: { kind: 'variable', idPrefix: 'variable-' },
  action: { kind: 'action', idPrefix: 'action-' },
  stack: { kind: 'stack', idPrefix: 'stack-' },
  stackBackdrop: { kind: 'stackBackdrop', idPrefix: 'stack-backdrop-' },
  workflowFrame: { kind: 'workflowFrame', idPrefix: 'workflow-frame-' },
  submission: { kind: 'submission', idPrefix: 'submission-' },
}

/** All entries as an array (stable order matching `NodeKind`). */
export const ID_PREFIX_ENTRIES: ReadonlyArray<IdPrefixEntry> = Object.values(ID_PREFIX_REGISTRY) as IdPrefixEntry[]

/**
 * Order matters for parsing: we match the longest prefix first so
 * `stack-backdrop-` wins over the shorter `stack-`. Derived from the
 * registry on load; static order is not required elsewhere.
 */
const ENTRIES_BY_LENGTH_DESC: ReadonlyArray<IdPrefixEntry> = [...ID_PREFIX_ENTRIES].sort(
  (a, b) => b.idPrefix.length - a.idPrefix.length,
)

const PREFIX_BY_KIND: Record<NodeKind, string> = (() => {
  const out = {} as Record<NodeKind, string>
  for (const entry of ID_PREFIX_ENTRIES) out[entry.kind] = entry.idPrefix
  return out
})()

/** Returns the id-prefix for a given kind (exposed for cases that still need the raw string). */
export function prefixForKind(kind: NodeKind): string {
  return PREFIX_BY_KIND[kind]
}

/**
 * Build a node ID from a kind and raw domain ID.
 * Example: `toNodeId('asset', 'abc-123')` → `'asset-abc-123'`.
 */
export function toNodeId(kind: NodeKind, id: string): string {
  return `${PREFIX_BY_KIND[kind]}${id}`
}

/**
 * Parse a node ID into `{ kind, id }`. Returns `null` if the prefix is unknown or
 * if the string is empty/missing a raw id portion.
 *
 * Iterates entries longest-first so `stack-backdrop-abc` is parsed as
 * `{ kind: 'stackBackdrop', id: 'abc' }` and not misparsed as
 * `{ kind: 'stack', id: 'backdrop-abc' }`.
 */
export function parseNodeId(nodeId: string): { kind: NodeKind; id: string } | null {
  if (typeof nodeId !== 'string' || nodeId.length === 0) return null
  for (const entry of ENTRIES_BY_LENGTH_DESC) {
    if (!nodeId.startsWith(entry.idPrefix)) continue
    // Prefix matched: commit to this kind. If the raw id is empty, return null
    // instead of falling back to a shorter prefix — this prevents `'stack-backdrop-'`
    // from being misparsed as `{ kind: 'stack', id: 'backdrop-' }`.
    if (nodeId.length === entry.idPrefix.length) return null
    return { kind: entry.kind, id: nodeId.slice(entry.idPrefix.length) }
  }
  return null
}

/** Convenience: returns the kind for a node ID, or `null` if the prefix is unknown. */
export function getNodeKind(nodeId: string): NodeKind | null {
  const parsed = parseNodeId(nodeId)
  return parsed ? parsed.kind : null
}

/**
 * Convenience: returns true iff the node ID's kind matches `kind`.
 *
 * Intentional edge cases:
 * - `isNodeOfKind('stack-backdrop-abc', 'stack')` is `false` (it's a stackBackdrop).
 * - `isNodeOfKind('asset-', 'asset')` is `false` (no raw id after the prefix).
 * - `isNodeOfKind('', 'asset')` is `false`.
 */
export function isNodeOfKind(nodeId: string, kind: NodeKind): boolean {
  return getNodeKind(nodeId) === kind
}
